import { CommonModule } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { select, Store } from '@ngrx/store'
import { AuthService } from '../../../services/auth.service'
import { resetPassword } from '../../../store/auth/auth.actions'
import { selectLoading } from '../../../store/auth/auth.state'
import { passwordStrengthValidator } from '../../../validators/passwordStrength.validators'
import { SharedModule } from '../../../shared/shared.module'
import { filter, switchMap, take } from 'rxjs'

@Component({
  selector: 'app-reset-password',
  imports: [ReactiveFormsModule, CommonModule, FormsModule, RouterModule, SharedModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent implements OnInit {
  store = inject(Store)
  isLoading$ = this.store.pipe(select(selectLoading))
  form: FormGroup
  passwordVisible = false
  confirmPasswordVisible = false
  passwordStrength = ''
  currentYear

  private email = ''
  private token = ''
  isTokenValid = false

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    // private jwtService: JwtService,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {
    this.currentYear = new Date().getFullYear() // Get current year
    this.form = this.formBuilder.group(
      {
        password: ['', [Validators.required, Validators.minLength(8), passwordStrengthValidator()]],
        confirmPassword: ['', [Validators.required]]
      },
      { validator: this.passwordsMatchValidator }
    )
  }

  ngOnInit() {
    // this.activatedRoute.queryParams.subscribe(params => {
    //   this.token = params['token'] // ✅ Correct way to access query parameters
    //   this.email = params['email']

    //   if (this.token && this.email) {
    //     this.authService.validateResetToken(this.token, this.email).subscribe({
    //       next: res => {
    //         if (res.valid) {
    //           this.isTokenValid = true
    //         }
    //       },
    //       error: () => {
    //         this.isTokenValid = false
    //       }
    //     })
    //   }
    // })

    this.activatedRoute.queryParams
      .pipe(
        take(1),
        filter(params => !!params['token'] && !!params['email']),
        switchMap(params => {
          this.token = params['token']
          this.email = params['email']
          return this.authService.validateResetToken(this.token, this.email)
        })
      )
      .subscribe({
        next: res => {
          this.isTokenValid = res.valid
        },
        error: () => {
          this.isTokenValid = false
        }
      })

    // Subscribe to password changes only once
    this.form.controls['password'].valueChanges.subscribe(value => {
      this.checkPasswordStrength(value)
      this.form.controls['confirmPassword'].updateValueAndValidity()
    })
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible
  }

  toggleConfirmPasswordVisibility() {
    this.confirmPasswordVisible = !this.confirmPasswordVisible
  }

  checkPasswordStrength(password: string) {
    this.passwordStrength = this.calculateStrength(password)
  }

  calculateStrength(password: string): string {
    const strongRegex = /(?=.*[0-9])(?=.*[!@#$%^&*])(?=.*[a-zA-Z]).{8,}/
    const mediumRegex = /(?=.*[0-9])(?=.*[a-zA-Z]).{8,}/
    const weakRegex = /.{6,}/

    if (strongRegex.test(password)) {
      return 'strong'
    } else if (mediumRegex.test(password)) {
      return 'medium'
    } else if (weakRegex.test(password)) {
      return 'weak'
    } else {
      return 'poor'
    }
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }

    if (this.form.valid) {
      this.store.dispatch(
        resetPassword({
          token: this.token,
          email: this.email,
          password: this.form.get('password')?.value // ✅ Get password value
        })
      )

      // this.router.navigate(['/auth/login'])
    }
  }

  get copyrightText(): SafeHtml {
    const copyrightSymbol = '\u00A9' // Unicode for ©
    const text =
      this.currentYear === 2025
        ? `Copyright ${copyrightSymbol} 2025 - <a href="https://avrtechno.com" class="hover-a">AVR Software Pvt. Ltd.</a>`
        : `Copyright ${copyrightSymbol} 2025 - ${this.currentYear} - <a href="https://avrtechno.com" class="hover-a">AVR Software Pvt. Ltd.</a>`

    return this.sanitizer.bypassSecurityTrustHtml(text)
  }

  getErrorMessage(field: string): string {
    if (!this.form.get(field)) return ''

    if (this.form.get(field)?.hasError('required')) {
      return 'This field is required'
    }

    if (this.form.get(field)?.hasError('minlength')) {
      return 'Minimum length is 8 characters'
    }

    if (field === 'confirmPassword' && this.form.errors?.['passwordsMismatch']) {
      return 'Passwords do not match'
    }

    return ''
  }

  // Custom validator function to check if passwords match
  passwordsMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const password = group.get('password')?.value
    const confirmPassword = group.get('confirmPassword')?.value

    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordsDoNotMatch: true }
    }

    return null
  }

  onPasswordInput(event: Event) {
    const inputElement = event.target as HTMLInputElement
    this.checkPasswordStrength(inputElement.value)
  }
}
